import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { NAV_LINKS } from "@/data/content";
import { Menu, X } from "lucide-react";

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: 0, duration: 1.2 });
  } else {
    el.scrollIntoView({ behavior: "smooth" });
  }
};

export default function NavDock() {
  const [active, setActive] = useState("hero");
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const ids = NAV_LINKS.map((l) => l.id);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => {
      observer.disconnect();
    };
  }, []);

  const handleLinkClick = (id) => {
    setIsOpen(false);
    scrollToId(id);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-bc/80 backdrop-blur-md border-b border-fluor/10 px-6 py-4 md:px-12">
      <div className="mx-auto flex max-w-7xl items-center justify-between">
        {/* Brand Logo */}
        <button
          data-testid="brand-logo"
          onClick={() => handleLinkClick("hero")}
          className="flex items-center gap-2 font-headers text-sm font-bold uppercase tracking-[0.25em] text-thinair transition-colors hover:text-fluor sm:text-base"
        >
          <span className="inline-block h-2 w-2 animate-blink bg-fluor" />
          IntelPreneur
        </button>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map((link) => (
            <button
              key={link.id}
              data-testid={`nav-${link.id}`}
              onClick={() => handleLinkClick(link.id)}
              className={`relative px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] transition-colors ${
                active === link.id ? "text-bc" : "text-thinair/70 hover:text-fluor"
              }`}
            >
              {active === link.id && (
                <motion.span
                  layoutId="nav-pill"
                  className="absolute inset-0 -z-10 bg-fluor"
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
              )}
              {link.label}
            </button>
          ))}
        </nav>

        {/* Mobile Hamburger Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex h-10 w-10 items-center justify-center border border-fluor/20 text-thinair hover:border-fluor/50 hover:text-fluor md:hidden"
          aria-label="Toggle Menu"
        >
          {isOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile Drawer Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="absolute top-[100%] left-0 right-0 border-b border-fluor/10 bg-[#001619]/95 px-6 py-8 shadow-xl backdrop-blur-lg md:hidden"
          >
            <nav className="flex flex-col gap-4">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.id}
                  onClick={() => handleLinkClick(link.id)}
                  className={`w-full py-3 text-left font-mono text-sm uppercase tracking-[0.2em] border-b border-fluor/5 transition-colors ${
                    active === link.id ? "text-fluor pl-2" : "text-thinair/70 hover:text-fluor"
                  }`}
                >
                  {link.label}
                </button>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
